import { useCallback, useEffect, useRef, useState } from 'react';
import {
    chunkToBase64,
    MicrophoneError,
    PCM_SAMPLE_RATE,
    startPcmRecorder,
    type PcmRecorder
} from './pcmRecorder';
import {
    reportVoiceSeconds,
    requestVoiceSession,
    type VoiceLanguage
} from './voiceApi';
import {
    TranslationRequestError,
    type TranslationFailure
} from './translationApi';
import { normalizeSourceLanguage } from './translationState';
import {
    applyVoiceMessage,
    dominantLanguage,
    EMPTY_TRANSCRIPT,
    transcriptText,
    type VoiceServerMessage,
    type VoiceTranscript
} from './voiceTranscript';

/**
 * `connecting` — the microphone is open, DeepL is not listening yet: what is
 * said now is lost, so the button says to wait. `finishing` — the recording
 * has stopped and DeepL is concluding the last words.
 */
export type VoiceState = 'idle' | 'connecting' | 'recording' | 'finishing';

export type VoiceFailure =
    TranslationFailure | 'microphoneDenied' | 'microphone' | 'interrupted';

export interface VoiceResult {
    /** What was said, in the language it was said in. */
    text: string;
    /** The same in German, when the recording was translated; else ''. */
    translation: string;
    /** The language chosen, or the one DeepL heard most. */
    sourceLang: VoiceLanguage;
}

/** A forgotten recording should not run up the contract all afternoon. */
const MAX_RECORDING_MS = 10 * 60 * 1000;
/** How long to wait for DeepL to conclude after the microphone stops. */
const FINISH_TIMEOUT_MS = 15 * 1000;

interface Run {
    recorder: PcmRecorder | null;
    socket: WebSocket | null;
    usageId: string;
    samples: number;
    /**
     * The chunks on their way, in order. DeepL wants a pause of at least half
     * the previous chunk's length before the next — a worklet chunk every
     * 200 ms keeps that by itself, the last short piece at the stop does not.
     */
    sending: Promise<void>;
    /** Not before this (ms since epoch) may the next chunk go out. */
    nextSendAt: number;
    transcript: VoiceTranscript;
    /** Null: DeepL detects the language. */
    sourceLang: VoiceLanguage | null;
    onDone: ((result: VoiceResult) => void) | null;
    timers: number[];
    finished: boolean;
}

/**
 * Speech to text through DeepL Voice: the microphone streams to DeepL while
 * the person speaks, and the text — and, on request, its German translation —
 * comes back as they go. One recording at a time.
 */
export function useVoiceDictation() {
    const [state, setState] = useState<VoiceState>('idle');
    const [transcript, setTranscript] =
        useState<VoiceTranscript>(EMPTY_TRANSCRIPT);
    const [error, setError] = useState<VoiceFailure | null>(null);
    const runRef = useRef<Run | null>(null);

    /**
     * End the run, once. With `deliver` the text DeepL has goes to the caller:
     * all of it after a proper end, else what it had concluded plus its last
     * guess — a dropped connection should not swallow the last sentence.
     */
    const finish = useCallback((deliver: boolean) => {
        const run = runRef.current;
        if (!run || run.finished) {
            return;
        }
        run.finished = true;
        runRef.current = null;
        run.timers.forEach((timer) => window.clearTimeout(timer));
        run.recorder?.stop();
        if (run.socket && run.socket.readyState <= WebSocket.OPEN) {
            run.socket.close();
        }
        if (run.usageId) {
            reportVoiceSeconds(run.usageId, run.samples / PCM_SAMPLE_RATE);
        }
        setState('idle');
        const { transcript: heard } = run;
        if (heard.error) {
            setError('unavailable');
        }
        const text = transcriptText(heard.source, !heard.ended);
        if (deliver && text !== '' && run.onDone) {
            const sourceLang =
                run.sourceLang ??
                normalizeSourceLanguage(dominantLanguage(heard) ?? '', 'de');
            run.onDone({
                text,
                // Detected German has nothing to be translated into: DeepL
                // hands it back as it was, which is no translation.
                translation:
                    sourceLang === 'de'
                        ? ''
                        : transcriptText(heard.target, !heard.ended),
                sourceLang
            });
        }
    }, []);

    // Leaving the form mid-recording releases the microphone and the session.
    useEffect(() => () => finish(false), [finish]);

    const stop = useCallback(() => {
        const run = runRef.current;
        if (!run) {
            return;
        }
        const recorder = run.recorder;
        run.recorder = null;
        if (run.socket?.readyState !== WebSocket.OPEN) {
            recorder?.stop();
            finish(true);
            return;
        }
        setState('finishing');
        run.timers.push(
            window.setTimeout(() => finish(true), FINISH_TIMEOUT_MS)
        );
        // The last words first: what the worklet still holds, and every chunk
        // still waiting for its turn. Then DeepL concludes what it holds as
        // tentative and ends.
        void (recorder?.finish() ?? Promise.resolve())
            .then(() => run.sending)
            .then(() => {
                if (
                    !run.finished &&
                    run.socket?.readyState === WebSocket.OPEN
                ) {
                    run.socket.send(
                        JSON.stringify({ end_of_source_media: {} })
                    );
                }
            });
    }, [finish]);

    const start = useCallback(
        async (
            options: {
                /** Null: DeepL detects the language. */
                sourceLang: VoiceLanguage | null;
                translate: boolean;
                projectId?: string;
            },
            onDone: (result: VoiceResult) => void
        ) => {
            if (runRef.current) {
                return;
            }
            const run: Run = {
                recorder: null,
                socket: null,
                usageId: '',
                samples: 0,
                sending: Promise.resolve(),
                nextSendAt: 0,
                transcript: EMPTY_TRANSCRIPT,
                sourceLang: options.sourceLang,
                onDone,
                timers: [],
                finished: false
            };
            runRef.current = run;
            setError(null);
            setTranscript(EMPTY_TRANSCRIPT);
            setState('connecting');

            // The microphone first: its permission prompt is the slow part,
            // and a refusal must not cost a DeepL session.
            try {
                run.recorder = await startPcmRecorder((chunk) => {
                    if (run.socket?.readyState !== WebSocket.OPEN) {
                        return;
                    }
                    run.sending = run.sending.then(async () => {
                        const wait = run.nextSendAt - Date.now();
                        if (wait > 0) {
                            await new Promise((resolve) =>
                                window.setTimeout(resolve, wait)
                            );
                        }
                        const socket = run.socket;
                        if (socket?.readyState !== WebSocket.OPEN) {
                            return;
                        }
                        socket.send(
                            JSON.stringify({
                                source_media_chunk: {
                                    data: chunkToBase64(chunk)
                                }
                            })
                        );
                        const samples = chunk.byteLength / 2;
                        run.samples += samples;
                        run.nextSendAt =
                            Date.now() + (samples / PCM_SAMPLE_RATE) * 500;
                    });
                });
            } catch (failure) {
                setError(
                    failure instanceof MicrophoneError &&
                        failure.reason === 'denied'
                        ? 'microphoneDenied'
                        : 'microphone'
                );
                finish(false);
                return;
            }
            if (run.finished) {
                run.recorder.stop();
                return;
            }

            let session;
            try {
                session = await requestVoiceSession({
                    sourceLang: options.sourceLang ?? undefined,
                    translate: options.translate,
                    projectId: options.projectId
                });
            } catch (failure) {
                setError(
                    failure instanceof TranslationRequestError
                        ? failure.reason
                        : 'unavailable'
                );
                finish(false);
                return;
            }
            if (run.finished) {
                return;
            }
            run.usageId = session.usageId;

            const socket = new WebSocket(
                `${session.streamingUrl}?token=${encodeURIComponent(session.token)}`
            );
            run.socket = socket;
            socket.onopen = () => {
                setState('recording');
                run.timers.push(window.setTimeout(stop, MAX_RECORDING_MS));
            };
            socket.onmessage = (event: MessageEvent<string>) => {
                let message: VoiceServerMessage;
                try {
                    message = JSON.parse(event.data) as VoiceServerMessage;
                } catch {
                    return;
                }
                run.transcript = applyVoiceMessage(run.transcript, message);
                setTranscript(run.transcript);
                if (run.transcript.ended) {
                    finish(true);
                }
            };
            socket.onclose = () => {
                if (!run.finished) {
                    // Closed before DeepL said it was done: keep the text,
                    // but say the recording broke off.
                    setError('interrupted');
                    finish(true);
                }
            };
        },
        [finish, stop]
    );

    return { state, transcript, error, start, stop };
}
